import React from 'react';
import { Transaction } from '../types';
import { motion } from 'framer-motion';
import { Coffee, ShoppingBag, Car, Zap, HeartPulse, Film, MoreHorizontal, ArrowRightLeft, Wallet, AlertCircle } from 'lucide-react';
interface TransactionListProps {
  transactions: Transaction[];
  onEdit: (transaction: Transaction) => void;
  emptyMessage?: string;
}
export function TransactionList({
  transactions,
  onEdit,
  emptyMessage = 'No transactions found'
}: TransactionListProps) {
  const getIcon = (category: string, type: string) => {
    if (type === 'savings') return Wallet;
    if (type === 'income') return ArrowRightLeft;
    switch (category.toLowerCase()) {
      case 'food':
        return Coffee;
      case 'shopping':
        return ShoppingBag;
      case 'transport':
        return Car;
      case 'bills':
        return Zap;
      case 'health':
        return HeartPulse;
      case 'entertainment':
        return Film;
      default:
        return MoreHorizontal;
    }
  };
  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(new Date(dateStr));
  };
  if (transactions.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-stone-500" data-id="element-78">
        <div className="w-16 h-16 bg-stone-800 rounded-full flex items-center justify-center mb-4" data-id="element-79">
          <MoreHorizontal size={32} opacity={0.5} data-id="element-80" />
        </div>
        <p data-id="element-81">{emptyMessage}</p>
      </div>;
  }
  return <div className="space-y-3" data-id="element-82">
      {transactions.map((t, index) => {
      const Icon = getIcon(t.category || '', t.type);
      const isExpense = t.type === 'expense';
      const isSavings = t.type === 'savings';
      const affectsBalance = t.affectsBalance !== false;
      return <motion.div key={t.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05
      }} onClick={() => onEdit(t)} className="bg-stone-800 hover:bg-stone-750 active:scale-[0.98] transition-all rounded-2xl p-4 flex items-center justify-between cursor-pointer border border-stone-700/30 shadow-sm" data-id="element-83">
            <div className="flex items-center gap-4" data-id="element-84">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isExpense ? 'bg-red-500/10 text-red-500' : isSavings ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`} data-id="element-85">
                <Icon size={20} data-id="element-86" />
              </div>
              <div data-id="element-87">
                <h3 className="text-stone-100 font-semibold" data-id="element-88">{t.name}</h3>
                <p className="text-stone-500 text-xs" data-id="element-89">
                  {t.category || t.type} • {formatDate(t.date)}
                </p>
                <div className="flex gap-2 mt-1" data-id="element-90">
                  {t.isDebt && <span className="inline-block px-2 py-0.5 bg-stone-700 rounded-md text-[10px] text-stone-300" data-id="element-91">
                      {t.type === 'income' ? 'Borrowed' : 'Lent'}
                    </span>}
                  {t.isDebt && !affectsBalance && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-md text-[10px] text-amber-400" data-id="element-92">
                      <AlertCircle size={10} data-id="element-93" />
                      Not {t.type === 'income' ? 'added' : 'removed'}
                    </span>}
                  {t.isDebt && affectsBalance && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 rounded-md text-[10px] text-emerald-400" data-id="element-94">
                      {t.type === 'income' ? 'Added to balance' : 'Removed from balance'}
                    </span>}
                </div>
              </div>
            </div>
            <div className="text-right" data-id="element-95">
              <p className={`font-bold ${isExpense ? 'text-stone-100' : isSavings ? 'text-emerald-400' : 'text-amber-500'}`} data-id="element-96">
                {isExpense ? '-' : '+'}
                {formatMoney(t.amount)}
              </p>
            </div>
          </motion.div>;
    })}
    </div>;
}